import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/app/dashboard/layout';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Loader2,
  MoreHorizontal,
  Edit,
  Trash2,
  CalendarClock,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOrganisation } from '@/App';
import { getPatients, createPatient, deletePatient } from '@/client/sdk.gen';
import type { PatientRead, PatientCreate } from '@/client/types.gen';
import { PatientCreateModal } from '@/components/ui/patient-create-modal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';

export default function PatientsPage() {
  const { toast } = useToast();
  const { organisationId } = useOrganisation();
  const [patients, setPatients] = useState<PatientRead[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch patients on mount and when organisationId changes
  useEffect(() => {
    if (organisationId) {
      fetchPatients();
    }
  }, [organisationId]);

  // Fetch patients function
  const fetchPatients = async () => {
    setIsLoadingPatients(true);
    try {
      const response = await getPatients();
      if (response.data) {
        setPatients(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load patients',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Handle create modal actions
  const handleCreateOnly = async (data: PatientCreate) => {
    try {
      await createPatient({
        body: data,
      });

      toast({
        title: 'Success',
        description: 'Patient created successfully',
      });

      setIsCreateModalOpen(false);
      fetchPatients();
    } catch (error) {
      console.error('Failed to create patient:', error);
      toast({
        title: 'Error',
        description: 'Failed to create patient',
        variant: 'destructive',
      });
    }
  };

  const handleCreateAndEdit = async (data: PatientCreate) => {
    try {
      const response = await createPatient({
        body: data,
      });

      if (response.data) {
        toast({
          title: 'Success',
          description: 'Patient created successfully',
        });

        // Navigate to the patient edit page
        navigate(`/dashboard/patients/${response.data.id}/edit`);
      }
    } catch (error) {
      console.error('Failed to create patient:', error);
      toast({
        title: 'Error',
        description: 'Failed to create patient',
        variant: 'destructive',
      });
    }
  };

  // Handle patient actions
  const handleEditPatient = (patient: PatientRead) => {
    // Navigate to edit page
    navigate(`/dashboard/patients/${patient.id}/edit`);
  };

  const handleDeletePatient = async (patientId: string) => {
    setPatientToDelete(patientId);
  };

  const confirmDeletePatient = async () => {
    if (!patientToDelete) return;

    try {
      await deletePatient({
        path: { patient_id: patientToDelete },
      });

      toast({
        title: 'Success',
        description: 'Patient deleted successfully',
      });

      // Refresh patients
      fetchPatients();
    } catch (error) {
      console.error('Failed to delete patient:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete patient',
        variant: 'destructive',
      });
    } finally {
      setPatientToDelete(null);
    }
  };

  const handleViewAppointments = (patientId: string) => {
    // For now, we'll just show a toast since appointment viewing is not implemented yet
    toast({
      title: 'View Appointments',
      description: `Viewing appointments for patient ID: ${patientId} (Feature coming soon)`,
    });
  };

  // Format date of birth
  const formatDateOfBirth = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Invalid date format:', error);
      return dateString;
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string): number => {
    try {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();

      // If not yet had birthday this year, subtract one year
      if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age;
    } catch (error) {
      console.error('Error calculating age:', error);
      return 0;
    }
  };

  // Get gender display text
  const getGenderLabel = (gender: string): string => {
    switch (gender.toLowerCase()) {
      case 'male':
        return 'Male';
      case 'female':
        return 'Female';
      case 'prefer_not_to_say':
        return 'Prefer not to say';
      default:
        return gender;
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Patients', href: '/dashboard/patients' },
      ]}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Patients</h1>
            <p className="text-muted-foreground">
              Create and manage your patients
            </p>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Patient
          </Button>
        </div>

        {/* Create Modal */}
        <PatientCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreateOnly={handleCreateOnly}
          onCreateAndEdit={handleCreateAndEdit}
        />

        {/* Delete Confirmation */}
        <AlertDialog
          open={!!patientToDelete}
          onOpenChange={() => setPatientToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                patient and all associated records.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeletePatient}
                className="bg-destructive text-destructive-foreground"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Patients Table */}
        {isLoadingPatients ? (
          <div className="rounded-md border p-8 shadow-sm flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
              <p className="text-muted-foreground">Loading patients...</p>
            </div>
          </div>
        ) : patients.length > 0 ? (
          <div className="rounded-md border shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Age / DOB</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Insurance</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patients.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell className="font-medium">
                      {patient.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{patient.email}</span>
                        <span className="text-sm text-muted-foreground">
                          {patient.phone}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {calculateAge(patient.date_of_birth)} years
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateOfBirth(patient.date_of_birth)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="py-1">
                        {getGenderLabel(patient.gender)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {patient.insurance_provider ? (
                        <div className="flex flex-col">
                          <span className="text-sm">
                            {patient.insurance_provider}
                          </span>
                          {patient.insurance_policy_number && (
                            <span className="text-xs text-muted-foreground">
                              Policy: {patient.insurance_policy_number}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Not provided
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleEditPatient(patient)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleViewAppointments(patient.id)}
                          >
                            <CalendarClock className="h-4 w-4 mr-2" />
                            View Appointments
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeletePatient(patient.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="rounded-md border shadow-sm">
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <h3 className="mt-2 text-lg font-semibold">No patients yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first patient to get started
              </p>
              <Button
                variant="outline"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                New Patient
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
